import React, { useState, useEffect } from "react";
import { Button, Col, Form, Row } from "reactstrap";
import * as yup from "yup";
import { setLocale } from "yup";
import { useForm, FormProvider } from "react-hook-form";
import useCurrentDealStore from "stores/DealStore";
import { useFromUser } from "stores/LocalStorageHelper";
import { saveSuccessAlert } from "utils/alertUtils";
import {
	DealStatus,
	PmtMode,
	downPaymentType,
	paymentMethod,
} from "constants/Constants";
import shallow from "zustand/shallow";
import dayjs from "dayjs";
import {
	CurrencyInput,
	CurrencyInputElement,
	SelectorElement,
} from "features/forms/components/InputElement";
import InputWrapper from "features/forms/components/InputWrapper";
import InputElement from "features/forms/components/InputElement";
import PropTypes from "prop-types";
import { yupResolver } from "@hookform/resolvers";
import DigitzsPayment from "../../../../../../services/digitzsService/DigitzsPayment";
import ChildrenModal from "../../../../../../components/modals/ChildrenModal";
import { deletePayment } from "../../../../../../account/AccountAPI";
import DigitzsAchPayment from "../../../../../../services/digitzsService/DigitzsAchPayment";
import DigitzsMerchantLogin from "../../../../../settings/subFeatures/settingsTabs/signUp/digitzs/DigitzsMerchantLogin";
import usePaymentProcessor from "../../../../../../hooks/usePaymentProcessor";
import XplorPayPayment from "../../../../../../services/xplorpayService/XplorPayPayment";
import XplorPayAchPayment from "../../../../../../services/xplorpayService/XplorPayAchPayment";

/**
 * MoneyDownForm component to handle adding and editing down payments.
 *
 * @param {Object} props - The component props.
 * @param {number} props.currentIndex - The current index of the down payment being edited.
 * @param {function} props.setCurrentIndex - Function to set the current index.
 * @param {function} props.toggle - Function to toggle the form visibility.
 */
export const MoneyDownForm = ({ currentIndex, setCurrentIndex, toggle }) => {
	// Payment processor detection
	const { isDigitzs, isXplorPay, hasProcessor } = usePaymentProcessor();
	console.log(isDigitzs, isXplorPay, hasProcessor);
	const [digitzsModal, setDigitzsModal] = useState(false);
	const toggleDigitzsModal = () => setDigitzsModal(!digitzsModal);

	const [xplorpayModal, setXplorpayModal] = useState(false);
	const toggleXplorpayModal = () => setXplorpayModal(!xplorpayModal);

	// Deal store
	const {
		downPayments,
		editDownPayments,
		type,
		calculate,
		customerID,
		dealID,
	} = useCurrentDealStore(
		(state) => ({
			downPayments: state.downPayments,
			editDownPayments: state.editDownPayments,
			type: state.deal.type,
			calculate: state.calculate,
			customerID: state.deal.customerID,
			dealID: state.deal.ID,
		}),
		shallow
	);

	const today = dayjs().format("YYYY-MM-DD");
	const dealerID = useFromUser("dealerID");
	const locationID = useFromUser("locationID");
	const userRecNum = useFromUser("ID");

	const [payment, setPayment] = useState(downPayments[currentIndex]);
	const [receiptNo, setReceiptNo] = useState("");
	const [datePaid, setDatePaid] = useState(today);
	const [amtPaid, setAmtPaid] = useState(0);
	const [convenienceFee, setConvenienceFee] = useState(0);
	const [checkNo, setCheckNo] = useState("");
	const [method, setMethod] = useState("cash");
	const [pmtType, setpmtType] = useState("Down");
	const [status, setStatus] = useState("completed");
	const [statusReason, setStatusReason] = useState("");
	const [note, setNote] = useState("");
	const [description, setDescription] = useState("");
	const [exportedToQB, setExportedToQB] = useState("");
	const [isExportToQB, setIsExportToQB] = useState(0);
	const [appVersion, setAppVersion] = useState("");
	const [hideCheck, sethideCheck] = useState(true);
	const [buttonText, setButtonText] = useState("Add");
	const [newPmt, setNewPmt] = useState(true);
	const [formTitle, setFormTitle] = useState([]);

	const changeText = (text) => setButtonText(text);

	const clearFields = () => {
		changeText("Add Payment");
		setReceiptNo("");
		setDatePaid("");
		setAmtPaid(0);
		setCheckNo("");
		setMethod("cash");
		setpmtType("Down");
		setStatusReason("");
		setStatus("completed");
		setNote("");
		setDescription("");
		setExportedToQB("");
		setIsExportToQB(0);
		setAppVersion("");
		setCurrentIndex(downPayments.length);
	};

	const getID = () => {
		if (downPayments[currentIndex] === undefined) {
			return null;
		} else if (downPayments[currentIndex].id === undefined) {
			return null;
		} else {
			return downPayments[currentIndex].id;
		}
	};
	const getNewDown = (ID) => ({
		...payment,
		id: ID,
		receiptNo,
		datePaid,
		amtPaid: parseFloat(amtPaid),
		convenienceFee: parseFloat(convenienceFee),
		checkNo,
		method,
		pmtMode: PmtMode.DOWN,
		pmtType,
		status: status,
		statusReason,
		note,
		description,
		exportedToQB,
		isExportToQB,
		createOn: dayjs().format("YYYY-MM-DD"),
		appVersion,
		locationID,
		dealerID,
		currentIndex,
		userRecNum,
	});

	const addPayment = (status) => {
		if (status === "" || status === null) {
			status = "Completed";
		}
		const id = getID();
		// const newDown = getNewDown(id);
		console.log("Current index is: " + currentIndex);
		console.log(status);
		const newDown = {
			...getNewDown(id),
			status: status, // Ensure the status state is used
		};
		console.log(newDown);
		const downList = () => {
			let obj = [...downPayments]; // Create a copy of downPayments
			obj[currentIndex] = newDown;
			return obj;
		};
		const newDownObj = downList();
		editDownPayments(newDownObj);
		setPayment(newDown);
		clearFields();
		saveSuccessAlert();
		calculate();
		toggle();
	};

	// Define custom error messages for different error types
	setLocale({
		mixed: {
			required: "Required",
		},
		string: {
			max: "Must be ${max} characters or less",
			length: "Required format is YYYY/MM/DD",
		},
	});

	// Define rules for each input field
	const schema = yup.object().shape({
		//receiptNo: yup.string().max(50).min(1).required(),
		//description: yup.string().required().max(50),
		// pmtType: yup.string().required().max(50),
		pmtMode: yup.string().required().max(50),
		//note: yup.string().required().max(50).min(1).nullable(),
	});

	// Define form validation parameters
	const methods = useForm({
		reValidateMode: "onBlur",
		resolver: yupResolver(schema),
	});

	useEffect(() => {
		if (currentIndex < downPayments.length) {
			setNewPmt(false);
			setFormTitle("Edit Payment ");
			setReceiptNo(downPayments[currentIndex]?.receiptNo || "");
			setDatePaid(downPayments[currentIndex]?.datePaid || "");
			setAmtPaid(downPayments[currentIndex]?.amtPaid || 0);
			setConvenienceFee(downPayments[currentIndex]?.convenienceFee || 0);
			setCheckNo(downPayments[currentIndex]?.checkNo || "");
			setMethod(downPayments[currentIndex]?.method || "");
			setpmtType(downPayments[currentIndex]?.pmtType || "");
			setNote(downPayments[currentIndex]?.note || "");
			setDescription(downPayments[currentIndex]?.description || "");
			setExportedToQB(downPayments[currentIndex]?.exportedToQB || "");
			setIsExportToQB(downPayments[currentIndex]?.isExportToQB || 0);
			setAppVersion(downPayments[currentIndex]?.appVersion || "");
			setStatus(downPayments[currentIndex]?.status || "completed");
			changeText("Save Payment");
		} else {
			const newDownPayment = {
				id: null,
				dealID: dealID,
				amtPaid,
				convenienceFee: parseFloat(convenienceFee),
				datePaid,
				receiptNo,
				method,
				checkNo,
				pmtType,
				pmtMode: PmtMode.DOWN,
				status: status,
				statusReason,
				note,
				description,
				appVersion,
				locationID,
				dealerID,
				createOn: new Date(),
				userRecNum,
			};
			console.log(newDownPayment);
			setPayment(newDownPayment);
			setNewPmt(true);
			setFormTitle("Add Payment");
		}

		// eslint-disable-next-line
	}, [currentIndex]);

	const handleMethod = (e) => {
		setMethod(e);
		if (
			e === "check" ||
			e === "cashierCheck" ||
			e === "echeck" ||
			e === "other"
		)
			sethideCheck(false);
		else sethideCheck(true);
	};
	const getRandomId = (min = 1000, max = 500000) => {
		min = Math.ceil(min);
		max = Math.floor(max);
		const num = Math.floor(Math.random() * (max - min + 1)) + min;

		if (!receiptNo) {
			setReceiptNo(num.toString());
		}
	};
	useEffect(() => {
		getRandomId();
		// eslint-disable-next-line
	}, [receiptNo]);
	const removePayment = () => {
		const ID = getID();
		console.log("Removing Payment with ID: " + ID);
		if (ID) {
			deletePayment(ID).then(
				(response) => {
					console.log(response);
					let obj = downPayments.filter((item) => item.id !== ID);
					editDownPayments(obj);

					deleteSuccessAlert();
					toggle();
					//					history.push(acctPaymentList);
				},
				(err) => {
					console.error(err);
					if (!err.isGeneralError) {
						showApiError(err, deleteFailAlert);
					}
				}
			);
		} else {
			const downList = () => {
				let obj = [...downPayments]; // Create a copy of downPayments
				obj.filter((item) => {
					return item.index === currentIndex;
				});
				return obj;
			};
			const newDownObj = downList();
			editDownPayments(newDownObj);
			clearFields();
			saveSuccessAlert();
			calculate();
			toggle();
		}
	};

	console.log({ method });

	return (
		<>
			<FormProvider {...methods}>
				<Form>
					<InputWrapper
						formTitle={formTitle}
						inputComponents={[
							<InputElement
								key="datePaid"
								// readOnly={type !== DealStatus.PENDING}
								readOnly={!newPmt}
								{...methods}
								value={datePaid}
								onChange={(e) => setDatePaid(e.target.value)}
								name="datePaid"
								label="Date Paid"
								type="date"
							/>,
							<SelectorElement
								key="method"
								{...methods}
								readOnly={!newPmt}
								locked={!newPmt}
								name="method"
								label="Methods"
								value={method}
								onChange={(e) => handleMethod(e.target.value)}
								options={paymentMethod}
							/>,
							<SelectorElement
								key="method"
								{...methods}
								readOnly={!newPmt}
								locked={!newPmt}
								name="pmtType"
								label="Type"
								value={pmtType}
								onChange={(e) => setpmtType(e.target.value)}
								options={downPaymentType}
							/>,
							<CurrencyInputElement
								key="amtPaid"
								readOnly={!newPmt}
								value={amtPaid}
								onChange={setAmtPaid}
								name="amtPaid"
								label="Amount"
								type="number"
							/>,
							<CurrencyInputElement
								key="convenienceFee"
								readOnly={!newPmt}
								value={convenienceFee}
								onChange={setConvenienceFee}
								name="convenienceFee"
								label="Convenience Fee"
								type="number"
							/>,
							// <CurrencyInputElement
							// 	key="totalPaid"
							// 	readOnly={true}
							// 	value={amtPaid + convenienceFee}
							// 	//onChange={setAmtPaid}
							// 	name="totalPaid"
							// 	label="Total Paid"
							// 	type="number"
							// />,
							<CurrencyInput
								disableValidation
								readOnly={true}
								value={amtPaid + convenienceFee}
								//onChange={setAmtPaid}
								name="totalPaid"
								label="Total Paid"
								type="number"
							/>,
							<InputElement
								key="receiptNo"
								{...methods}
								value={receiptNo}
								onChange={(e) => setReceiptNo(e.target.value)}
								name="receiptNo"
								label="Receipt No"
								type="text"
							/>,
							<InputElement
								{...methods}
								key="checkNo"
								disabled={hideCheck}
								value={checkNo}
								onChange={(e) => setCheckNo(e.target.value)}
								name="checkNo"
								label="Check No"
								type="text"
							/>,
							<InputElement
								{...methods}
								key="status"
								disabled={true}
								value={status}
								name="status"
								label="Status"
								type="text"
							/>,
							<InputElement
								key="description"
								readOnly={type !== DealStatus.PENDING}
								{...methods}
								value={description}
								onChange={(e) => setDescription(e.target.value)}
								colSize="col-sm-9"
								name="description"
								label="Description"
								type="text"
							/>,
							<InputElement
								key="note"
								readOnly={type !== DealStatus.PENDING}
								{...methods}
								value={note}
								onChange={(e) => setNote(e.target.value)}
								colSize="col-sm-12"
								name="note"
								label="Note"
								type="textarea"
							/>,
							/* Payment Processor Section - Digitzs */
							hasProcessor &&
								isDigitzs &&
								(method === "Credit Card" ||
									method === "Debit Card" ||
									method === "ACH") && (
									<Col sm="3" className="text-center">
										<ChildrenModal
											size="lg"
											modal={digitzsModal}
											toggle={toggleDigitzsModal}
										>
											<Row className="mt-2 justify-content-left">
												<Col>
													{method === "ACH" && (
														<DigitzsAchPayment
															dealID={dealID}
															pmtAmount={amtPaid}
															pmtObj={payment}
															setPmtObj={setPayment}
															addPayment={addPayment}
														/>
													)}
													{(method === "Credit Card" ||
														method === "Debit Card") && (
														<DigitzsPayment
															dealID={dealID}
															pmtAmount={amtPaid}
															pmtObj={payment}
															setPmtObj={setPayment}
															addPayment={addPayment}
														/>
													)}
													<DigitzsMerchantLogin />
												</Col>
											</Row>
										</ChildrenModal>
										<Col>
											{method === "ACH" && (
												<Button onClick={toggleDigitzsModal}>
													Process ACH
												</Button>
											)}
											{(method === "Credit Card" ||
												method === "Debit Card") && (
												<Button onClick={toggleDigitzsModal}>
													Process Credit Card
												</Button>
											)}
										</Col>
									</Col>
								),
							/* Payment Processor Section - XplorPay */
							hasProcessor &&
								isXplorPay &&
								(method === "Credit Card" ||
									method === "Debit Card" ||
									method === "ACH") && (
									<Col sm="3" className="text-center">
										<ChildrenModal
											size="lg"
											modal={xplorpayModal}
											toggle={toggleXplorpayModal}
										>
											<Row className="mt-2 justify-content-left">
												<Col>
													{method === "ACH" ? (
														<XplorPayAchPayment
															dealID={dealID}
															pmtAmount={amtPaid}
															pmtObj={payment}
															setPmtObj={setPayment}
															addPayment={addPayment}
															toggle={toggleXplorpayModal}
														/>
													) : (
														<XplorPayPayment
															dealID={dealID}
															pmtAmount={amtPaid}
															pmtObj={payment}
															setPmtObj={setPayment}
															addPayment={addPayment}
															toggle={toggleXplorpayModal}
														/>
													)}
												</Col>
											</Row>
										</ChildrenModal>
										<Col>
											{method === "ACH" && (
												<Button color="primary" onClick={toggleXplorpayModal}>
													Process ACH
												</Button>
											)}
											{(method === "Credit Card" ||
												method === "Debit Card") && (
												<Button color="primary" onClick={toggleXplorpayModal}>
													Process Payment
												</Button>
											)}
										</Col>
									</Col>
								),
						]}
						buttons={
							<div className="d-flex flex-column align-items-center mt-2">
								{hasProcessor && (
									<span
										className={`badge ${
											isXplorPay ? "badge-success" : "badge-info"
										} p-2 mb-2`}
									>
										<i className="nc-icon nc-check-2 mr-1" />
										Active Processor: {isXplorPay ? "XplorPay" : "Digitzs"}
									</span>
								)}
								<div>
									<Button
										color="primary"
										onClick={() => addPayment("completed")}
									>
										{buttonText === "Save Payment" ? (
											<i className="nc-icon nc-check-2"></i>
										) : (
											<i className="nc-icon nc-simple-add"></i>
										)}
										{status === "void" ? "Restore" : buttonText}
									</Button>
									{/* <div className="btn btn-link btn-md" onClick={clearFields}>
									Reset
								</div> */}

									{payment?.id && payment?.status !== "void" && (
										<Button
											active={type === "Pending"}
											color="danger "
											onClick={() => {
												setStatus("void");
												addPayment("void");
											}}
										>
											Void
										</Button>
									)}
									{payment?.id && payment?.status === "void" && (
										<Button
											active={type === "pending"}
											color="danger "
											onClick={() => {
												removePayment();
											}}
										>
											Remove
										</Button>
									)}
								</div>
							</div>
						}
					/>
				</Form>
			</FormProvider>
		</>
	);
};

MoneyDownForm.propTypes = {
	currentIndex: PropTypes.number.isRequired,
	setCurrentIndex: PropTypes.func.isRequired,
	toggle: PropTypes.func.isRequired,
};

export default MoneyDownForm;
